import { unwrap } from "@sagot/api-client";
import { consoleRoutes, webappPublicUrl } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Dialog, DialogContent, EmptyState, ErrorState, Field, Icon, Input, Skeleton, toast, useCopy } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../session/workspace-context";
import type { CustomDomainOut } from "../shared/api-types";
import { wsKeys } from "../shared/query-keys";

interface ShareTarget {
  url: string | null;
  customDomain: boolean;
}

/**
 * A live custom domain for this agent (or workspace-wide) wins over the default `/chat/<slug>` link
 * (DEC-040). Only an `active` domain counts: its certificate is issued and `https://<host>/` opens
 * the chat (HCU-004, BUG-SCOPE-004), whereas a domain that has only found its DNS may not answer yet.
 */
export function resolveShareTarget(domains: readonly CustomDomainOut[], defaultUrl: string | null, agentId: string): ShareTarget {
  const verified = domains.filter((d) => d.status === "active");
  const domain = verified.find((d) => d.agent_id === agentId) ?? verified.find((d) => d.agent_id === null);
  if (domain) return { url: `https://${domain.hostname}`, customDomain: true };
  return { url: defaultUrl, customDomain: false };
}

function useShareTarget(agentId: string, enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const { summary } = useWorkspaceContext();
  return useQuery({
    queryKey: [...wsKeys.customDomains(wsId), "share", agentId],
    enabled,
    queryFn: async () => {
      const [domains, channels] = await Promise.all([
        unwrap(api.GET("/v1/custom-domains")),
        unwrap(api.GET("/v1/agents/{agent_id}/channels", { params: { path: { agent_id: agentId } } })),
      ]);
      const webapp = channels.find((c) => c.type === "webapp" && c.enabled);
      return resolveShareTarget(domains, webapp ? webappPublicUrl(summary.slug) : null, agentId);
    },
  });
}

function ShareBody({ agentId, target }: { agentId: string; target: ShareTarget }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { copy } = useCopy();
  if (!target.url) {
    return (
      <EmptyState
        icon="app-window"
        headingLevel={3}
        title={t("dashboard.share.unavailableTitle")}
        description={t("dashboard.share.unavailableBody")}
        action={
          <Button asChild variant="secondary">
            <AppLink href={consoleRoutes.channels(summary.slug, agentId)}>{t("dashboard.share.openChannels")}</AppLink>
          </Button>
        }
      />
    );
  }
  const url = target.url;
  const onCopy = async () => {
    if (await copy(url)) toast.success(t("dashboard.share.copied"));
    else toast.error(t("dashboard.share.copyFailed"));
  };
  return (
    <div className="space-y-4">
      <Field label={t("dashboard.share.urlLabel")} hint={target.customDomain ? t("dashboard.share.customDomain") : t("dashboard.share.defaultDomain")}>
        <div className="flex gap-2">
          <Input readOnly value={url} className="font-mono text-xs" onFocus={(event) => { event.currentTarget.select(); }} />
          <Button variant="secondary" onClick={() => void onCopy()}>
            <Icon name="copy" />
            {t("dashboard.share.copy")}
          </Button>
        </div>
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Icon name="external-link" />
            {t("dashboard.share.openWebApp")}
          </a>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <AppLink href={consoleRoutes.widgetStudio(summary.slug, agentId)}>
            <Icon name="eye" />
            {t("dashboard.share.previewWidget")}
          </AppLink>
        </Button>
      </div>
    </div>
  );
}

interface ShareDialogProps {
  agent: { id: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** `share-modal` "Share your assistant": public chat URL + copy, open web app, preview widget. */
export function ShareDialog({ agent, open, onOpenChange }: ShareDialogProps) {
  const { t } = useTranslation();
  const target = useShareTarget(agent.id, open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t("dashboard.share.title")} description={t("dashboard.share.description", { agent: agent.name })} closeLabel={t("common.close")} className="max-w-lg">
        {target.isPending ? (
          <div className="space-y-2" aria-hidden="true">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ) : target.isError ? (
          <ErrorState headingLevel={3} title={t("dashboard.share.loadError")} onRetry={() => void target.refetch()} retryLabel={t("common.retry")} />
        ) : (
          <ShareBody agentId={agent.id} target={target.data} />
        )}
      </DialogContent>
    </Dialog>
  );
}
